import {
  type BrowserCommand,
  type BrowserSessionSnapshot,
  type BrowserTabSnapshot,
  type DevTunnelRecord,
  type EnvironmentId,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { type MutableRefObject } from "react";
import {
  type BrowserOptionsAction,
  useBrowserSessionActions,
} from "./BrowserPreviewPanel.sessionControlActions";
import { useBrowserSessionRestart } from "./BrowserPreviewPanel.sessionRestart";

export { type BrowserOptionsAction };

interface BrowserSessionControlsOptions {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly activeTabUrl: string | null;
  readonly devTunnelRecordsRef: MutableRefObject<ReadonlyArray<DevTunnelRecord>>;
  readonly environmentId: EnvironmentId;
  readonly handleBrowserError: (error: unknown) => void;
  readonly invalidateInFlightBrowserCommands: () => void;
  readonly lifecycleAction: BrowserOptionsAction | null;
  readonly readBrowserAddressValue: (override?: string | null) => string;
  readonly runCommand: (command: BrowserCommand) => Promise<boolean>;
  readonly session: BrowserSessionSnapshot | null;
  readonly sessionOpenInitialUrlRef: MutableRefObject<string>;
  readonly setBrowserRuntimeError: (value: string | null) => void;
  readonly setLifecycleAction: (value: BrowserOptionsAction | null) => void;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}

interface BrowserSessionControls {
  readonly handleBrowserOptionsAction: (action: BrowserOptionsAction) => Promise<void>;
  readonly restartBrowserSessionAtUrl: (initialUrl: string) => Promise<boolean>;
}

export function useBrowserSessionControls({
  activeTab,
  activeTabUrl,
  devTunnelRecordsRef,
  environmentId,
  handleBrowserError,
  invalidateInFlightBrowserCommands,
  lifecycleAction,
  readBrowserAddressValue,
  runCommand,
  session,
  sessionOpenInitialUrlRef,
  setBrowserRuntimeError,
  setLifecycleAction,
  setSessionSnapshot,
  threadId,
  threadRef,
}: BrowserSessionControlsOptions): BrowserSessionControls {
  const { restartBrowserSessionAtUrl } = useBrowserSessionRestart({
    devTunnelRecordsRef,
    environmentId,
    handleBrowserError,
    invalidateInFlightBrowserCommands,
    session,
    setBrowserRuntimeError,
    setLifecycleAction,
    setSessionSnapshot,
    threadId,
    threadRef,
  });

  const { handleBrowserOptionsAction } = useBrowserSessionActions({
    activeTab,
    activeTabUrl,
    environmentId,
    handleBrowserError,
    invalidateInFlightBrowserCommands,
    lifecycleAction,
    readBrowserAddressValue,
    runCommand,
    session,
    sessionOpenInitialUrlRef,
    setBrowserRuntimeError,
    setLifecycleAction,
    setSessionSnapshot,
    threadId,
    threadRef,
  });

  return {
    handleBrowserOptionsAction,
    restartBrowserSessionAtUrl,
  };
}
